import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Component, Customer, Proposal, ProposalItem } from '@shared/schema';
import {
  ArrowLeft,
  FileText,
  Download,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  FileSignature,
  FileArchive,
  ExternalLink,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'wouter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DocumentViewer } from '@/components/installations/document-viewer';
import { FileData } from '@/components/installations/document-upload';

export default function ProposalDetailPage() {
  const [, navigate] = useLocation();
  const { id } = useParams();
  const proposalId = Number(id);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [totalCost, setTotalCost] = useState(0);
  const [margin, setMargin] = useState(0);
  const [sellPrice, setSellPrice] = useState(0);

  // Status options with labels and colors
  const statusOptions = [
    { value: 'draft', label: 'Draft', icon: Clock, color: 'text-yellow-500' },
    { value: 'sent', label: 'Sent', icon: Send, color: 'text-blue-500' },
    { value: 'accepted', label: 'Accepted', icon: CheckCircle, color: 'text-green-500' },
    { value: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-500' },
  ];

  const { data: proposal, isLoading: isLoadingProposal } = useQuery<Proposal>({
    queryKey: [`/api/proposals/${proposalId}`],
    enabled: !!proposalId && !isNaN(proposalId),
  });

  const { data: proposalItems, isLoading: isLoadingItems } = useQuery<ProposalItem[]>({
    queryKey: [`/api/proposals/${proposalId}/items`],
    enabled: !!proposalId && !isNaN(proposalId),
  });

  const { data: components } = useQuery<Component[]>({
    queryKey: ['/api/components'],
  });

  const { data: customers } = useQuery<Customer[]>({
    queryKey: ['/api/customers'],
  });

  // Fetch documents related to this proposal
  const { data: proposalDocuments = [] } = useQuery<FileData[]>({
    queryKey: [`/api/proposals/${proposalId}/documents`],
    enabled: !!proposalId && !isNaN(proposalId),
  });

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      return await apiRequest('PATCH', `/api/proposals/${proposalId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/proposals/${proposalId}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/proposals'] });
      toast({
        title: 'Proposal status updated',
        description: 'The proposal status has been successfully updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating proposal',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (proposal) {
      setTotalCost(Number(proposal.totalCost));
      setMargin(Number(proposal.margin));
      setSellPrice(Number(proposal.sellPrice));
    }
  }, [proposal]);

  const handleStatusChange = (value: string) => {
    statusMutation.mutate(value);
  };

  const customer = customers?.find((c) => proposal && c.id === proposal.customerId);

  const getComponentById = (id: number) => {
    return components?.find((c) => c.id === id);
  };

  // Group items by category for better organization
  const groupedItems: Record<string, ProposalItem[]> = {};
  proposalItems?.forEach((item) => {
    const component = getComponentById(item.componentId);
    if (component) {
      if (!groupedItems[component.category]) {
        groupedItems[component.category] = [];
      }
      groupedItems[component.category].push(item);
    }
  });

  // Get current status display info
  const currentStatus = proposal?.status
    ? statusOptions.find((s) => s.value === proposal.status)
    : statusOptions[0];

  if (isLoadingProposal || !proposal) {
    return (
      <div className="w-full">
        <main className="p-4 md:p-6 overflow-x-hidden">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="w-full">
      <main className="p-4 md:p-6 overflow-x-hidden">
        <div className="max-w-7xl mx-auto space-y-6 w-full 2xl:max-w-[1400px] ultrawide:max-w-[1800px]">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => navigate('/proposals')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold">{proposal.name}</h1>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select defaultValue={proposal.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      {currentStatus?.icon && (
                        <currentStatus.icon className={`h-4 w-4 ${currentStatus.color}`} />
                      )}
                      <span>{currentStatus?.label}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <status.icon className={`h-4 w-4 ${status.color}`} />
                        <span>{status.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                className="w-full sm:w-auto"
                onClick={() => navigate(`/loa-form/${proposalId}`)}
              >
                <FileSignature className="mr-2 h-4 w-4" />
                Letter of Authority
              </Button>
              <Button className="w-full sm:w-auto">
                <Download className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 xl:grid-cols-4 2xl:grid-cols-6">
            <Card className="md:col-span-2 xl:col-span-3 2xl:col-span-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Proposal Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 xl:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground">Customer</Label>
                    <div className="font-medium text-lg">{customer?.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {customer?.email && <div>{customer.email}</div>}
                      {customer?.phone && <div>{customer.phone}</div>}
                      {customer?.address && <div>{customer.address}</div>}
                    </div>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">Date</Label>
                    <div className="font-medium">
                      {proposal.createdAt &&
                        new Date(proposal.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                    </div>
                  </div>

                  {proposal.notes && (
                    <div className="sm:col-span-2 xl:col-span-1">
                      <Label className="text-muted-foreground">Notes</Label>
                      <div className="mt-1 text-sm whitespace-pre-wrap">{proposal.notes}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="md:col-span-1 xl:col-span-1 2xl:col-span-2">
              <CardHeader>
                <CardTitle>Financial Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
                  <div className="2xl:col-span-1">
                    <Label className="text-muted-foreground">Total Cost</Label>
                    <div className="font-medium text-lg">
                      £{Number(proposal.totalCost).toFixed(2)}
                    </div>
                  </div>
                  <div className="2xl:col-span-1">
                    <Label className="text-muted-foreground">Margin</Label>
                    <div className="font-medium text-lg">£{Number(proposal.margin).toFixed(2)}</div>
                  </div>
                  <div className="2xl:col-span-1">
                    <Label className="text-muted-foreground">Sell Price</Label>
                    <div className="font-medium text-xl text-primary">
                      £{Number(proposal.sellPrice).toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Document Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileArchive className="h-5 w-5 text-primary" />
                Proposal Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DocumentViewer
                documents={Array.isArray(proposalDocuments) ? proposalDocuments : []}
                title="Proposal Documents"
                emptyMessage="No documents attached to this proposal"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Components & Equipment</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingItems ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-32 bg-gray-200 rounded"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedItems).map(([category, items]) => (
                    <div key={category} className="space-y-3">
                      <h3 className="text-lg font-medium capitalize">{category}</h3>
                      <div className="border rounded-md overflow-x-auto">
                        <Table className="min-w-full table-fixed">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-1/4 2xl:w-1/5">Component</TableHead>
                              <TableHead className="w-1/4 2xl:w-1/5">Model</TableHead>
                              <TableHead className="text-right w-1/6 2xl:w-1/5">
                                Unit Cost
                              </TableHead>
                              <TableHead className="text-right w-1/6 2xl:w-1/5">Quantity</TableHead>
                              <TableHead className="text-right w-1/6 2xl:w-1/5">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {items.map((item) => {
                              const component = getComponentById(item.componentId);
                              return (
                                <TableRow key={item.id}>
                                  <TableCell className="font-medium">
                                    {component?.name || 'Unknown'}
                                  </TableCell>
                                  <TableCell>{component?.model || 'Unknown'}</TableCell>
                                  <TableCell className="text-right">
                                    £{Number(item.unitCost).toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-right">{item.quantity}</TableCell>
                                  <TableCell className="text-right">
                                    £{Number(item.totalCost).toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {/* Category subtotal row */}
                            <TableRow className="bg-muted/50">
                              <TableCell colSpan={4} className="text-right font-medium">
                                {category.charAt(0).toUpperCase() + category.slice(1)} Subtotal
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                £
                                {items
                                  .reduce((sum, item) => sum + Number(item.totalCost), 0)
                                  .toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}

                  {/* Grand total */}
                  <div className="flex justify-end pt-4">
                    <div className="w-full max-w-md space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Total Component Cost:</span>
                        <span className="font-medium">
                          £{Number(proposal.totalCost).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Margin:</span>
                        <span className="font-medium">£{Number(proposal.margin).toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between text-lg">
                        <span className="font-bold">Total Sell Price:</span>
                        <span className="font-bold text-primary">
                          £{Number(proposal.sellPrice).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
